/**
 * @file RequireAuth.tsx
 * @description Guard для приватных страниц admin (phase04 шаг 3).
 *
 * Логика:
 * - status === 'loading' → крутилка (полноэкранная, чтобы не дёргать layout).
 * - status === 'unauthenticated' → redirect на /login с сохранением исходного
 *   пути в state.from (LoginPage после успеха вернёт сюда).
 * - 0 контекстов → /no-context (тех. ошибка, об этом должен знать платформа-админ).
 * - ≥2 контекстов и нет активного → /select-context.
 * - Иначе → пропускаем (выдаём <Outlet />).
 *
 * ВАЖНО: системные «pass-through»-страницы (/select-context, /no-context, /403)
 * сами проверяют пред-условия и должны рендериться без редиректа от guard'а,
 * иначе при попадании на тот же путь Navigate уйдёт в no-op и Outlet не
 * отрендерится — экран будет пустой.
 *
 * RBAC-проверки (RequirePermission, RequireRole) — отдельный шаг 5.
 */

import { Loader2 } from "lucide-react";
import { Navigate, Outlet, useLocation } from "react-router";

import { useAuth } from "../../auth/hooks";

const SYSTEM_PASSTHROUGH = new Set([
  "/select-context",
  "/no-context",
  "/403",
]);

export function RequireAuth() {
  const { status, me } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (status === "unauthenticated" || !me) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // На системных страницах не делаем переадресации — иначе self-redirect
  // приводит к рендеру null вместо Outlet'а (пустой экран).
  if (SYSTEM_PASSTHROUGH.has(location.pathname)) {
    return <Outlet />;
  }

  if (me.contexts.length === 0) {
    return <Navigate to="/no-context" replace />;
  }

  // Адрес кабинета (`/shop/:slug`, `/platform`) сам выбирает кабинет вкладки
  // (RequireContextType, phase19.31) — кабинет сессии ему не нужен.
  const cabinetPath = /^\/(shop\/[^/]+|platform)(\/|$)/.test(location.pathname);
  if (!me.activeContext && me.contexts.length > 1 && !cabinetPath) {
    return <Navigate to="/select-context" replace />;
  }

  return <Outlet />;
}
